// pages/rental/BikeList.jsx — Ink & Lime Design System
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ROUTES } from '../../utils/constants';
import { ChevronLeft, ChevronRight, AlertCircle, Calendar, Clock, Search, X, MapPin } from 'lucide-react';

// ── Design Tokens ─────────────────────────────────────────────────────────
const T = {
  ink:     '#0D0D0F',
  ink2:    '#1A1A20',
  slate:   '#2D2D38',
  muted:   '#6B6B7A',
  line:    '#E8E8F0',
  surface: '#F5F5FA',
  white:   '#FFFFFF',
  lime:    '#C8F23C',
  limeD:   '#A8D020',
  teal:    '#00BFA5',
  coral:   '#FF6D3A',
};

// ── Static Store Definitions ───────────────────────────────────────────────
const STORES = [
  { key: 'all',      label: 'All Stores',       storeNames: [] },
  { key: 'kharadi',  label: 'Vegatas Kharadi',   storeNames: ['Vegatas Khardi', 'Kharadi Pune', 'Vegatas Kharadi'] },
  { key: 'bavdhan',  label: 'Vegatas Bavdhan',   storeNames: ['Vegatas Bavdhan', 'Pune bavdhan'] },
  { key: 'fcroad',   label: 'Vegatas FC Road',   storeNames: ['Vegatas FC Road', 'Starbuks FC Road'] },
  { key: 'kothrud',  label: 'Vegatas Kothrud',   storeNames: ['Vegatas-Kothrud', 'Vegatas Kothrud'] },
];

const BASE_URL = import.meta.env.VITE_BASE_URL || 'http://localhost:8081';

// ── Helpers ───────────────────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return '';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  return new Date(2000, 0, 1, +h, +m).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};
const fmtPrice = (p) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(p || 0);

// ── BikeImage ─────────────────────────────────────────────────────────────
const BikeImage = ({ src, alt }) => {
  const [err, setErr] = useState(false);
  return err || !src ? (
    <div style={{ width: '100%', height: '100%', background: T.slate, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48 }}>🏍️</div>
  ) : (
    <img src={src} alt={alt} onError={() => setErr(true)}
      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform .4s' }}
    />
  );
};

const getImgUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${BASE_URL}/uploads/${path.replace(/^\/?(uploads\/)?/, '')}`;
};

// ── Store Filter Bar ──────────────────────────────────────────────────────
const StoreFilterBar = ({ activeStore, onSelect }) => (
  <div style={{ marginBottom: 20 }}>
    <p style={{
      fontSize: 11, fontWeight: 800, letterSpacing: 1.2, textTransform: 'uppercase',
      color: T.muted, marginBottom: 10,
    }}>Filter by store</p>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      {STORES.map(store => {
        const isActive = activeStore === store.key;
        return (
          <button
            key={store.key}
            onClick={() => onSelect(store.key)}
            style={{
              padding: '8px 16px',
              borderRadius: 999,
              border: `1.5px solid ${isActive ? T.ink : T.line}`,
              background: isActive ? T.ink : T.white,
              color: isActive ? T.lime : T.muted,
              fontSize: 13,
              fontWeight: isActive ? 800 : 600,
              cursor: 'pointer',
              transition: 'all .15s',
              whiteSpace: 'nowrap',
            }}
          >
            {store.label}
          </button>
        );
      })}
    </div>
  </div>
);

// ── Bike Card ─────────────────────────────────────────────────────────────
const BikeCard = ({ bike, onBook, isAuthenticated, getBikeDisplayName }) => {
  const [hov, setHov] = useState(false);
  const hasPackages = bike.packages && bike.packages.length > 0;
  const mainPkg = hasPackages ? bike.packages[0] : null;

  return (
    <article
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: T.white, borderRadius: 20, overflow: 'hidden',
        border: `1.5px solid ${hov ? T.lime : T.line}`,
        transition: 'border-color .2s, transform .25s, box-shadow .25s',
        transform: hov ? 'translateY(-5px)' : 'none',
        boxShadow: hov ? '0 20px 50px rgba(0,0,0,.09)' : '0 2px 8px rgba(0,0,0,.04)',
        display: 'flex', flexDirection: 'column',
      }}
    >
      {/* Image */}
      <div style={{ position: 'relative', height: 180, overflow: 'hidden', background: T.slate }}>
        <div style={{ width: '100%', height: '100%', transform: hov ? 'scale(1.06)' : 'scale(1)', transition: 'transform .4s' }}>
          <BikeImage src={getImgUrl(bike.mainImageUrl)} alt={getBikeDisplayName(bike)} />
        </div>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(13,13,15,.55) 0%, transparent 55%)' }} />
        {bike.vehicleTypeName && (
          <div style={{
            position: 'absolute', top: 12, left: 12,
            background: T.ink, color: T.lime,
            fontSize: 10, fontWeight: 800, padding: '3px 10px', borderRadius: 20, letterSpacing: .8,
          }}>
            {bike.vehicleTypeName}
          </div>
        )}
        {bike.storeName && (
          <div style={{
            position: 'absolute', top: 12, right: 12,
            background: T.white, color: T.ink,
            fontSize: 10, fontWeight: 800, padding: '4px 10px', borderRadius: 20,
            boxShadow: '0 2px 8px rgba(0,0,0,.15)',
          }}>
            {bike.storeName}
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: '16px 18px 18px', display: 'flex', flexDirection: 'column', flex: 1, gap: 12 }}>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 800, color: T.ink, marginBottom: 6, lineHeight: 1.2 }}>
            {getBikeDisplayName(bike)}
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {bike.registrationNumber && (
              <div style={{ fontSize: 11, color: T.muted, display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ fontWeight: 700, color: T.slate }}>Reg:</span> {bike.registrationNumber}
              </div>
            )}
            {bike.storeName && (
              <div style={{ fontSize: 11, color: T.muted, display: 'flex', alignItems: 'center', gap: 5 }}>
                <MapPin size={11} color={T.muted} style={{ flexShrink: 0 }} />
                {bike.storeName}
              </div>
            )}
          </div>
        </div>

        {/* Price + Book Now */}
        <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          {mainPkg ? (
            <div>
              <div style={{ fontSize: 24, fontWeight: 900, color: T.ink, lineHeight: 1 }}>
                ₹{mainPkg.price}
                {mainPkg.days === 0 && <span style={{ fontSize: 12, fontWeight: 700, color: T.muted }}>/hr</span>}
              </div>
              <p style={{ fontSize: 11, color: T.muted, marginTop: 4 }}>for {mainPkg.daysDisplay}</p>
            </div>
          ) : (
            <p style={{ fontSize: 12, color: T.muted }}>No packages available</p>
          )}
          <button onClick={() => onBook(bike)}
            onMouseEnter={e => { e.currentTarget.style.opacity = '.88'; }}
            onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
            style={{
              padding: '12px 22px', borderRadius: 999, border: 'none',
              background: T.ink, color: T.lime,
              fontSize: 13, fontWeight: 800, cursor: 'pointer',
              transition: 'opacity .2s', letterSpacing: .4, whiteSpace: 'nowrap',
            }}>
            {!isAuthenticated ? 'Login to Book' : 'Book Now'}
          </button>
        </div>
      </div>
    </article>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────
export default function BikeList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, authCheckComplete, authLoading } = useAuth();

  const [bikes,           setBikes]           = useState([]);
  const [allBikes,        setAllBikes]        = useState([]);
  const [loading,         setLoading]         = useState(false);
  const [error,           setError]           = useState('');
  const [currentPage,     setCurrentPage]     = useState(0);
  const [totalPages,      setTotalPages]      = useState(0);
  const [pageSize]                            = useState(12);
  const [searchTerm,      setSearchTerm]      = useState('');
  const [isSearching,     setIsSearching]     = useState(false);
  const [activeStore,     setActiveStore]     = useState('all');   // ← new

  const sf = useMemo(() => ({
    startDate:     searchParams.get('startDate')     || '',
    endDate:       searchParams.get('endDate')       || '',
    pickupTime:    searchParams.get('pickupTime')    || '10:00',
    dropoffTime:   searchParams.get('dropoffTime')   || '19:00',
    cityId:        searchParams.get('cityId')        || null,
    vehicleTypeId: searchParams.get('vehicleTypeId') || null,
    search:        searchParams.get('search')        || '',
  }), [searchParams]);

  const duration = useMemo(() => {
    if (!sf.startDate || !sf.endDate) return null;
    const start = new Date(`${sf.startDate}T${sf.pickupTime}`);
    const end   = new Date(`${sf.endDate}T${sf.dropoffTime}`);
    const hours = (end - start) / 3_600_000;
    return {
      totalHours:     hours,
      days:           Math.floor(hours / 24),
      remainingHours: Math.floor(hours % 24),
      isHourly:       hours <= 6,
      isDaily:        hours > 6  && hours <= 144,
      isWeekly:       hours > 144,
    };
  }, [sf]);

  const getBikeName = useCallback((bike) => {
    if (bike.brand && bike.model) return `${bike.brand} ${bike.model}`;
    return bike.name || bike.registrationNumber || 'Unknown Bike';
  }, []);

  // ── Store filter helper ──────────────────────────────────────────────────
  const storeMatchesBike = useCallback((storeKey, bike) => {
    if (storeKey === 'all') return true;
    const storeDef = STORES.find(s => s.key === storeKey);
    if (!storeDef) return true;
    const name = (bike.storeName || '').toLowerCase();
    return storeDef.storeNames.some(n => name.includes(n.toLowerCase()) || n.toLowerCase().includes(name));
  }, []);

  // Fetch bikes (paginated)
  const fetchBikes = useCallback(async (page = 0) => {
    const { startDate, endDate, cityId, vehicleTypeId } = sf;
    if (!startDate || !endDate) { setError('Start & end date required'); return; }
    if (!cityId)                { setError('City required — please go back and select one.'); return; }
    setLoading(true); setError('');
    try {
      const p = new URLSearchParams({ startDate, endDate, cityId, page: String(page), size: String(pageSize) });
      if (vehicleTypeId) p.set('vehicleTypeId', vehicleTypeId);
      const res  = await fetch(`${BASE_URL}/api/bikes/available?${p}`, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setBikes(data?.content || []);
      setTotalPages(data?.totalPages || 0);
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError('Unable to load bikes. Please try again.');
      setBikes([]);
    } finally {
      setLoading(false);
    }
  }, [sf, pageSize]);

  // Fetch ALL bikes for search
  const fetchAllBikes = useCallback(async () => {
    const { startDate, endDate, cityId } = sf;
    if (!startDate || !endDate || !cityId) return;
    setIsSearching(true);
    try {
      const p = new URLSearchParams({ startDate, endDate, cityId, page: '0', size: '1000' });
      const res  = await fetch(`${BASE_URL}/api/bikes/available?${p}`, { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setAllBikes(data?.content || []);
    } catch {}
  }, [sf]);

  useEffect(() => {
    if (sf.startDate && sf.endDate && sf.cityId) fetchBikes(0);
  }, [sf.startDate, sf.endDate, sf.cityId, sf.vehicleTypeId]);

  useEffect(() => {
    if (searchTerm.trim()) {
      const t = setTimeout(() => { allBikes.length === 0 ? fetchAllBikes() : setIsSearching(true); }, 300);
      return () => clearTimeout(t);
    } else {
      setIsSearching(false);
    }
  }, [searchTerm, allBikes.length, fetchAllBikes]);

  // ── Filtered bikes (search + store) ─────────────────────────────────────
  const filteredBikes = useMemo(() => {
    let pool = searchTerm.trim()
      ? (isSearching ? allBikes : bikes)
      : bikes;

    // text search
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      pool = pool.filter(b =>
        getBikeName(b).toLowerCase().includes(term) ||
        (b.registrationNumber || '').toLowerCase().includes(term) ||
        (b.storeName || '').toLowerCase().includes(term)
      );
    }

    // store filter
    if (activeStore !== 'all') {
      pool = pool.filter(b => storeMatchesBike(activeStore, b));
    }

    return pool;
  }, [bikes, allBikes, searchTerm, isSearching, getBikeName, activeStore, storeMatchesBike]);

  const handleBook = useCallback((bike) => {
    if (!authCheckComplete || !sf.startDate || !sf.endDate) return;
    const params = new URLSearchParams({
      bikeId: bike.id, bikeName: getBikeName(bike),
      bikeImage: bike.mainImageUrl || '', registrationNumber: bike.registrationNumber || '',
      storeName: bike.storeName || '', categoryId: bike.categoryId || '',
      startDate: sf.startDate, endDate: sf.endDate,
      pickupTime: sf.pickupTime, dropoffTime: sf.dropoffTime,
      cityId: sf.cityId, vehicleTypeId: sf.vehicleTypeId || '',
      totalHours: String(duration?.totalHours || 0),
      bookingType: duration?.isHourly ? 'HOURLY' : 'DAILY',
      hasPackageSelected: 'false',
      timestamp: new Date().toISOString(), source: 'bike-list',
    });
    const url = `${ROUTES.RENTAL}/checkout?${params}`;
    if (!isAuthenticated) {
      navigate(`${ROUTES.LOGIN}?returnUrl=${encodeURIComponent(url)}`);
      return;
    }
    navigate(url);
  }, [sf, duration, navigate, isAuthenticated, authCheckComplete, getBikeName]);

  // ── Loading screen ──
  if (authLoading && !authCheckComplete) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: T.surface, fontFamily: "'DM Sans',sans-serif" }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', border: `3px solid ${T.lime}`, borderTopColor: 'transparent', animation: 'spin .8s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ fontSize: 15, fontWeight: 700, color: T.muted }}>Loading bikes…</p>
          <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: T.surface, fontFamily: "'DM Sans',sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700;800;900&family=Space+Grotesk:wght@700&display=swap');
        * { box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from{opacity:0;transform:translateY(16px);}to{opacity:1;transform:none;} }
        @keyframes pulse { 0%,100%{opacity:1;}50%{opacity:.35;} }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-thumb { background: ${T.line}; border-radius: 4px; }
      `}</style>

      {/* ── HERO HEADER ── */}
      <div style={{ background: T.ink, position: 'relative', overflow: 'hidden', padding: '44px 24px 40px' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `linear-gradient(${T.slate} 1px,transparent 1px),linear-gradient(90deg,${T.slate} 1px,transparent 1px)`, backgroundSize: '52px 52px', opacity: .22 }} />
        <div style={{ position: 'absolute', top: -80, right: -60, width: 380, height: 380, background: T.lime, borderRadius: '50%', filter: 'blur(100px)', opacity: .06 }} />
        <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center', position: 'relative' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(200,242,60,.1)', border: '1px solid rgba(200,242,60,.25)', borderRadius: 40, padding: '5px 14px', marginBottom: 18 }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.lime, display: 'block', animation: 'pulse 1.5s infinite' }} />
            <span style={{ fontSize: 10, fontWeight: 800, color: T.lime, letterSpacing: 1.5, textTransform: 'uppercase' }}>Choose Your Ride</span>
          </div>
          <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 'clamp(30px,5vw,52px)', fontWeight: 700, color: T.white, marginBottom: 10, lineHeight: 1.1 }}>
            Available{' '}<span style={{ color: T.lime }}>Bikes for You</span>
          </h1>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,.45)', marginBottom: 20 }}>All bikes verified, insured &amp; ready to ride</p>
          {sf.startDate && sf.endDate && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 10,
              background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.12)',
              borderRadius: 40, padding: '8px 18px', fontSize: 13, color: 'rgba(255,255,255,.7)',
            }}>
              <Calendar size={14} />
              <span>{fmtDate(sf.startDate)} {fmtTime(sf.pickupTime)}</span>
              <span style={{ color: T.lime }}>→</span>
              <span>{fmtDate(sf.endDate)} {fmtTime(sf.dropoffTime)}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '24px 20px' }}>

        {/* Search bar */}
        <div style={{
          background: T.white, borderRadius: 16, padding: '14px 18px',
          border: `1.5px solid ${T.line}`, marginBottom: 20,
          display: 'flex', gap: 12, alignItems: 'center',
          boxShadow: '0 2px 12px rgba(0,0,0,.05)',
        }}>
          <Search size={18} color={T.muted} style={{ flexShrink: 0 }} />
          <input
            type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search by name, registration, or store…"
            style={{ flex: 1, border: 'none', outline: 'none', fontSize: 14, fontWeight: 500, color: T.ink, background: 'transparent' }}
          />
          {searchTerm && (
            <>
              <span style={{ fontSize: 12, color: T.muted, whiteSpace: 'nowrap' }}>
                {filteredBikes.length} result{filteredBikes.length !== 1 ? 's' : ''}
              </span>
              <button onClick={() => { setSearchTerm(''); setIsSearching(false); }}
                style={{ border: 'none', background: T.surface, borderRadius: 8, padding: '5px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: T.muted, fontSize: 12, fontWeight: 600 }}>
                <X size={13} /> Clear
              </button>
            </>
          )}
        </div>

        {/* ── STORE FILTER ── */}
        <StoreFilterBar activeStore={activeStore} onSelect={setActiveStore} />

        {/* Result count row */}
        {!loading && !error && bikes.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <p style={{ fontSize: 13, color: T.muted }}>
              <strong style={{ fontSize: 16, fontWeight: 900, color: T.ink }}>{filteredBikes.length}</strong> bike{filteredBikes.length !== 1 ? 's' : ''} found
              {activeStore !== 'all' && (
                <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: T.lime }}>
                  {STORES.find(s => s.key === activeStore)?.label}
                </span>
              )}
              {isSearching && <span style={{ marginLeft: 8, color: T.lime, fontSize: 11, fontWeight: 700 }}>GLOBAL SEARCH</span>}
            </p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {activeStore !== 'all' && (
                <button onClick={() => setActiveStore('all')}
                  style={{ fontSize: 12, fontWeight: 700, color: T.muted, background: 'none', border: `1px solid ${T.line}`, padding: '6px 14px', borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <X size={12} /> Clear store filter
                </button>
              )}
              <button onClick={() => navigate('/rental/search')}
                style={{ fontSize: 12, fontWeight: 700, color: T.muted, background: 'none', border: `1px solid ${T.line}`, padding: '6px 14px', borderRadius: 10, cursor: 'pointer' }}>
                ← Modify Search
              </button>
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '64px 0' }}>
            <div style={{ width: 44, height: 44, borderRadius: '50%', border: `3px solid ${T.lime}`, borderTopColor: 'transparent', animation: 'spin .8s linear infinite', marginBottom: 14 }} />
            <span style={{ fontSize: 14, fontWeight: 700, color: T.muted }}>Loading bikes…</span>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ background: T.white, borderRadius: 16, padding: '20px 24px', border: `1.5px solid ${T.line}`, marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <AlertCircle size={20} color={T.coral} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              <p style={{ fontSize: 14, fontWeight: 700, color: T.ink, marginBottom: 10 }}>{error}</p>
              <button onClick={() => navigate('/rental')}
                style={{ padding: '9px 20px', borderRadius: 10, background: T.ink, color: T.lime, border: 'none', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
                Back to Search →
              </button>
            </div>
          </div>
        )}

        {/* Bikes grid */}
        {!loading && !error && filteredBikes.length > 0 && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 18, marginBottom: 32 }}>
              {filteredBikes.map(bike => (
                <BikeCard
                  key={bike.id}
                  bike={bike}
                  onBook={handleBook}
                  isAuthenticated={isAuthenticated}
                  getBikeDisplayName={getBikeName}
                />
              ))}
            </div>

            {/* Pagination (only in non-search, non-store-filtered mode) */}
            {!isSearching && activeStore === 'all' && totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={() => currentPage > 0 && fetchBikes(currentPage - 1)} disabled={currentPage === 0}
                  style={{
                    width: 40, height: 40, borderRadius: 10, border: `1.5px solid ${T.line}`,
                    background: T.white, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? .35 : 1,
                  }}>
                  <ChevronLeft size={18} color={T.muted} />
                </button>
                {Array.from({ length: totalPages }).map((_, idx) => (
                  <button key={idx} onClick={() => fetchBikes(idx)}
                    style={{
                      width: 40, height: 40, borderRadius: 10,
                      background: idx === currentPage ? T.ink : T.white,
                      color: idx === currentPage ? T.lime : T.slate,
                      fontSize: 13, fontWeight: 800, cursor: 'pointer',
                      border: `1.5px solid ${idx === currentPage ? T.ink : T.line}`,
                      transform: idx === currentPage ? 'scale(1.08)' : 'scale(1)',
                      transition: 'all .15s',
                    }}>
                    {idx + 1}
                  </button>
                ))}
                <button onClick={() => currentPage < totalPages - 1 && fetchBikes(currentPage + 1)} disabled={currentPage === totalPages - 1}
                  style={{
                    width: 40, height: 40, borderRadius: 10, border: `1.5px solid ${T.line}`,
                    background: T.white, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: currentPage === totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages - 1 ? .35 : 1,
                  }}>
                  <ChevronRight size={18} color={T.muted} />
                </button>
                <span style={{ fontSize: 12, color: T.muted, marginLeft: 6 }}>
                  Page {currentPage + 1} of {totalPages}
                </span>
              </div>
            )}
          </>
        )}

        {/* No results after store/search filter */}
        {!loading && !error && filteredBikes.length === 0 && (bikes.length > 0 || allBikes.length > 0) && (
          <div style={{ textAlign: 'center', padding: '64px 24px', background: T.white, borderRadius: 20, border: `1.5px solid ${T.line}` }}>
            <div style={{ fontSize: 52, marginBottom: 16 }}>🔍</div>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: T.ink, marginBottom: 6 }}>
              {activeStore !== 'all' && !searchTerm
                ? `No bikes at ${STORES.find(s => s.key === activeStore)?.label}`
                : 'No bikes match your search'}
            </h3>
            <p style={{ fontSize: 14, color: T.muted, marginBottom: 20 }}>
              {activeStore !== 'all' ? 'Try another store or view all bikes' : 'Try different keywords'}
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              {activeStore !== 'all' && (
                <button onClick={() => setActiveStore('all')}
                  style={{ padding: '11px 28px', borderRadius: 12, background: T.ink, color: T.lime, border: 'none', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
                  Show All Stores
                </button>
              )}
              {searchTerm && (
                <button onClick={() => { setSearchTerm(''); setIsSearching(false); }}
                  style={{ padding: '11px 28px', borderRadius: 12, background: T.surface, color: T.ink, border: `1.5px solid ${T.line}`, fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
                  Clear Search
                </button>
              )}
            </div>
          </div>
        )}

        {/* No bikes at all */}
        {!loading && !error && bikes.length === 0 && !searchTerm && (
          <div style={{ textAlign: 'center', padding: '64px 24px', background: T.white, borderRadius: 20, border: `1.5px solid ${T.line}` }}>
            <div style={{ fontSize: 52, marginBottom: 16 }}>🏍️</div>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: T.ink, marginBottom: 6 }}>No bikes available</h3>
            <p style={{ fontSize: 14, color: T.muted, marginBottom: 20 }}>No bikes available for the selected dates</p>
            <button onClick={() => navigate(ROUTES.RENTAL)}
              style={{ padding: '11px 28px', borderRadius: 12, background: T.ink, color: T.lime, border: 'none', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
              Search Again →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
